import React, { useState } from "react";
import { Redirect } from "react-router-dom";
import { Button, Loader, Pagination } from "@culturehq/components";
import client from "@culturehq/client";

import { useFetch } from "app/hooks";
import { t, Trans } from "app/utils";

import PageHeader from "components/display/PageHeader";
import ErrorMessage from "components/common/ErrorMessage";

import SurveyTable from "./SurveyTable";
import SurveyEditModal from "./SurveyEditModal";
import SurveyManageableTableRow from "./SurveyManageableTableRow";

const SurveysManage = () => {
  const [page, setPage] = useState(1);
  const { error, fetching, json: { surveys = [], pagination }, onUpdate } = useFetch(
    () => client.listProfileManageableWidgetSurveys({ page }),
    [page]
  );

  const [redirect, setRedirect] = useState(null);
  const onEditSuccess = survey => setRedirect(`/surveys/${survey.id}/edit`);

  const onSurveyActivated = async survey => {
    const { survey: nextSurvey } = await client.activateSurvey({ surveyId: survey.id });

    onUpdate(current => ({
      ...current,
      surveys: current.surveys.map(currentSurvey => (
        currentSurvey.id === survey.id ? nextSurvey : currentSurvey
      ))
    }));
  };

  const onSurveyEnded = async survey => {
    await client.deactivateSurvey({ surveyId: survey.id });

    onUpdate(current => ({
      ...current,
      surveys: current.surveys.map(currentSurvey => (
        currentSurvey.id === survey.id ? { ...currentSurvey, active: false } : currentSurvey
      ))
    }));
  };

  const onSurveyDeleted = async survey => {
    await client.deleteSurvey({ surveyId: survey.id });

    onUpdate(current => ({
      ...current,
      surveys: current.surveys.filter(({ id }) => id !== survey.id)
    }));
  };

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  return (
    <div className="SurveyManage">
      <PageHeader title={t("Manage surveys")}>
        <Trans>Here you can manage the surveys to which you have administrator rights.</Trans>
      </PageHeader>

      {error && <ErrorMessage error={error} />}

      <SurveyEditModal onEditSuccess={onEditSuccess}>
        {onTrigger => (
          <Button icon="plus" primary onClick={onTrigger}>
            <Trans>Create New Survey</Trans>
          </Button>
        )}
      </SurveyEditModal>
      <br />
      <br />

      <Loader loading={fetching}>
        {!error && (
          <>
            {surveys && surveys.length > 0 ? (
              <SurveyTable displayStatus>
                {surveys.map(survey => (
                  <SurveyManageableTableRow
                    key={survey.id}
                    survey={survey}
                    onSurveyActivated={onSurveyActivated}
                    onSurveyEnded={onSurveyEnded}
                    onSurveyDeleted={onSurveyDeleted}
                  />
                ))}
              </SurveyTable>
            ) : (
              <p>
                <Trans>No manageable surveys.</Trans>
              </p>
            )}
            <Pagination {...pagination} onClick={setPage} />
          </>
        )}
      </Loader>
    </div>
  );
};

export default SurveysManage;
